import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHOTOS = [
  "https://customer-assets.emergentagent.com/job_3f61d9e7-42af-4a09-aa24-c85ae968e99c/artifacts/m90mwzx9_3.jpg",
  "https://customer-assets.emergentagent.com/job_3f61d9e7-42af-4a09-aa24-c85ae968e99c/artifacts/hljf33y8_4.jpg",
  "https://customer-assets.emergentagent.com/job_3f61d9e7-42af-4a09-aa24-c85ae968e99c/artifacts/ilsbc8et_2.jpg",
  "https://customer-assets.emergentagent.com/job_3f61d9e7-42af-4a09-aa24-c85ae968e99c/artifacts/2g9xxp8l_1bmfV3L4qEj9DNV6uQS1g.png",
  "https://customer-assets.emergentagent.com/job_3f61d9e7-42af-4a09-aa24-c85ae968e99c/artifacts/zqj3gnf8_3IkmCR9LjUJmnl8HVqf9c.png"
];

const AdminArticleEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    category_slug: '',
    excerpt: '',
    content: '',
    meta_description: '',
    primary_keyword: '',
    secondary_keywords: [],
    hero_image: PHOTOS[0],
    hero_image_alt: '',
    tags: [],
    author: 'The Lady Hustler Society Club',
    is_published: true,
    is_featured: false,
    is_pillar_guide: false,
    is_series: false,
    series_name: '',
    opening_line: ''
  });

  const isEditing = Boolean(id);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/articles/${id}`);
      setFormData({
        ...response.data,
        secondary_keywords: response.data.secondary_keywords || [],
        tags: response.data.tags || []
      });
    } catch (error) {
      toast.error('Article not found');
      navigate('/admin/articles');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !isEditing) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }

    // Update category_slug when category changes
    if (field === 'category') {
      const cat = categories.find(c => c.name === value);
      if (cat) {
        setFormData(prev => ({ ...prev, category_slug: cat.slug }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        secondary_keywords: typeof formData.secondary_keywords === 'string' 
          ? formData.secondary_keywords.split(',').map(s => s.trim()).filter(Boolean)
          : formData.secondary_keywords,
        tags: typeof formData.tags === 'string'
          ? formData.tags.split(',').map(s => s.trim()).filter(Boolean)
          : formData.tags
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/articles/${id}`, payload, getAuthHeaders());
        toast.success('Article updated');
      } else {
        await axios.post(`${API_URL}/api/articles`, payload, getAuthHeaders());
        toast.success('Article created');
      }
      navigate('/admin/articles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="space-y-4">
            <div className="h-8 w-48 loading-skeleton"></div>
            <div className="h-12 loading-skeleton"></div>
            <div className="h-64 loading-skeleton"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/articles" className="p-2 hover:bg-[#F2E1E1] text-[#767C91] hover:text-[#9D6E6D]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-heading text-3xl text-[#2C2C2C]" data-testid="article-edit-title">
                {isEditing ? 'Edit Article' : 'New Article'}
              </h1>
              {isEditing && (
                <a 
                  href={`/en/blog/${formData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-sm text-[#9D6E6D] hover:underline"
                >
                  View live article →
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isEditing && (
              <a 
                href={`/en/blog/${formData.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 border border-[#E2E8F0] text-[#767C91] hover:border-[#9D6E6D] hover:text-[#9D6E6D]"
              >
                <Eye className="w-4 h-4" /> Preview
              </a>
            )}
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-[#9D6E6D] hover:bg-[#2C2C2C] text-white rounded-none"
              data-testid="save-article-btn"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Article'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
              <h2 className="font-heading text-xl text-[#2C2C2C] mb-4">Basic Information</h2>
              
              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">TITLE *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Enter article title..."
                  className="rounded-none border-[#E2E8F0]"
                  required
                  data-testid="article-title-input"
                />
              </div>

              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">SLUG *</label>
                <div className="flex items-center gap-2">
                  <span className="text-[#767C91] text-sm">/en/blog/</span>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    placeholder="article-slug"
                    className="rounded-none border-[#E2E8F0]"
                    required
                    data-testid="article-slug-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">CATEGORY *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="w-full px-4 py-2 border border-[#E2E8F0] bg-white text-sm"
                    required
                    data-testid="article-category-select"
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">AUTHOR</label>
                  <Input
                    value={formData.author}
                    onChange={(e) => handleChange('author', e.target.value)}
                    className="rounded-none border-[#E2E8F0]"
                  />
                </div>
              </div>

              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">EXCERPT *</label>
                <Textarea
                  value={formData.excerpt}
                  onChange={(e) => handleChange('excerpt', e.target.value)}
                  placeholder="Brief description of the article..."
                  className="rounded-none border-[#E2E8F0] min-h-[100px]"
                  required
                  data-testid="article-excerpt-input"
                />
              </div>
            </div>

            {/* Content */}
            <div className="bg-white border border-[#E2E8F0] p-6">
              <h2 className="font-heading text-xl text-[#2C2C2C] mb-4">Content</h2>
              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">OPENING LINE</label>
                <Input
                  value={formData.opening_line || ''}
                  onChange={(e) => handleChange('opening_line', e.target.value)}
                  placeholder="The powerful opening line..."
                  className="rounded-none border-[#E2E8F0] mb-4"
                />
              </div>
              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">ARTICLE CONTENT (HTML) *</label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => handleChange('content', e.target.value)}
                  placeholder="<p>Your article content in HTML...</p>"
                  className="rounded-none border-[#E2E8F0] min-h-[400px] font-mono text-sm"
                  required
                  data-testid="article-content-input"
                />
                <p className="text-xs text-[#767C91] mt-2">
                  Supports HTML formatting: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;blockquote&gt;, etc.
                </p>
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
              <h2 className="font-heading text-xl text-[#2C2C2C] mb-4">SEO</h2>
              
              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">META DESCRIPTION *</label>
                <Textarea
                  value={formData.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  placeholder="150-160 characters for search results..."
                  className="rounded-none border-[#E2E8F0]"
                  maxLength={160}
                  required
                />
                <p className="text-xs text-[#767C91] mt-1">{formData.meta_description.length}/160 characters</p>
              </div>

              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">PRIMARY KEYWORD *</label>
                <Input
                  value={formData.primary_keyword}
                  onChange={(e) => handleChange('primary_keyword', e.target.value)}
                  placeholder="main keyword phrase"
                  className="rounded-none border-[#E2E8F0]"
                  required
                />
              </div>

              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">SECONDARY KEYWORDS</label>
                <Input
                  value={Array.isArray(formData.secondary_keywords) ? formData.secondary_keywords.join(', ') : formData.secondary_keywords}
                  onChange={(e) => handleChange('secondary_keywords', e.target.value)}
                  placeholder="keyword 1, keyword 2, keyword 3"
                  className="rounded-none border-[#E2E8F0]"
                />
                <p className="text-xs text-[#767C91] mt-1">Separate with commas</p>
              </div>

              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">TAGS</label>
                <Input
                  value={Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  className="rounded-none border-[#E2E8F0]"
                />
                <p className="text-xs text-[#767C91] mt-1">Separate with commas</p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Hero Image */}
            <div className="bg-white border border-[#E2E8F0] p-6">
              <h2 className="font-heading text-xl text-[#2C2C2C] mb-4">Hero Image</h2>
              
              <img 
                src={formData.hero_image} 
                alt="Hero preview" 
                className="w-full h-40 object-cover mb-4"
              />
              
              <div className="grid grid-cols-5 gap-2 mb-4">
                {PHOTOS.map((photo, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleChange('hero_image', photo)}
                    className={`border-2 ${formData.hero_image === photo ? 'border-[#9D6E6D]' : 'border-transparent'}`}
                  >
                    <img src={photo} alt="" className="w-full h-10 object-cover" />
                  </button>
                ))}
              </div>

              <div>
                <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">IMAGE ALT TEXT</label>
                <Input
                  value={formData.hero_image_alt}
                  onChange={(e) => handleChange('hero_image_alt', e.target.value)}
                  placeholder="Descriptive alt text..."
                  className="rounded-none border-[#E2E8F0]"
                />
              </div>
            </div>

            {/* Status */}
            <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
              <h2 className="font-heading text-xl text-[#2C2C2C] mb-4">Status</h2>
              
              <div className="flex items-center justify-between">
                <label className="font-label text-xs text-[#767C91] tracking-widest">PUBLISHED</label>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) => handleChange('is_published', checked)}
                  data-testid="article-published-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="font-label text-xs text-[#767C91] tracking-widest">FEATURED</label>
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => handleChange('is_featured', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="font-label text-xs text-[#767C91] tracking-widest">PILLAR GUIDE</label>
                <Switch
                  checked={formData.is_pillar_guide}
                  onCheckedChange={(checked) => handleChange('is_pillar_guide', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="font-label text-xs text-[#767C91] tracking-widest">SERIES ARTICLE</label>
                <Switch
                  checked={formData.is_series}
                  onCheckedChange={(checked) => handleChange('is_series', checked)}
                />
              </div>

              {formData.is_series && (
                <div>
                  <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">SERIES NAME</label>
                  <Input
                    value={formData.series_name || ''}
                    onChange={(e) => handleChange('series_name', e.target.value)}
                    placeholder="Women Who Changed the Rules"
                    className="rounded-none border-[#E2E8F0]"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminArticleEdit;
